import { createFileRoute, redirect } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { Layout } from "@/components/layout"
import { AdminNav } from "@/components/admin-nav"
import { Spinner } from "@/components/ui/spinner"
import { WaitlistEntryRow } from "@/components/admin/waitlist-entry-row"
import { listWaitlistEntries } from "@/lib/api/admin-waitlist"

type Filter = "all" | "pending" | "invited"

function WaitlistPage() {
  const [filter, setFilter] = useState<Filter>("pending")

  const { data: entries = [], isPending } = useQuery({
    queryKey: ["admin", "waitlist"],
    queryFn: listWaitlistEntries,
  })

  const filtered = entries.filter((e) => {
    if (filter === "pending") return !e.invitedAt
    if (filter === "invited") return !!e.invitedAt
    return true
  })

  const counts = {
    all: entries.length,
    pending: entries.filter((e) => !e.invitedAt).length,
    invited: entries.filter((e) => !!e.invitedAt).length,
  }

  const filterLabels: { key: Filter; label: string }[] = [
    { key: "pending", label: `Pending (${counts.pending})` },
    { key: "invited", label: `Invited (${counts.invited})` },
    { key: "all", label: `All (${counts.all})` },
  ]

  return (
    <Layout>
      <div className="pb-12">
        <div className="mb-4 pb-2">
          <p className="text-xs tracking-widest text-muted-foreground uppercase">
            Admin
          </p>
        </div>
        <AdminNav />
        <div className="mt-6 mb-6 border-b pb-4">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground">
            Invite Management
          </h1>
        </div>

        <div className="mb-4 flex gap-2">
          {filterLabels.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                filter === key
                  ? "bg-secondary font-medium text-secondary-foreground"
                  : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {isPending ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {filter === "pending"
              ? "No pending waitlist entries."
              : "No entries found."}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map((entry) => (
              <WaitlistEntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}

export const Route = createFileRoute("/_authenticated/admin/waitlist")({
  beforeLoad: ({ context }) => {
    const flags = (context as { userFlags?: { isAdmin?: boolean | null } })
      .userFlags
    if (!flags?.isAdmin) {
      throw redirect({ to: "/stories" })
    }
  },
  component: WaitlistPage,
})
